import * as THREE from 'three';
import { MarchingCubes } from 'three/addons/objects/MarchingCubes.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import {
  ToonShader1,
  ToonShader2,
  ToonShaderHatching,
  ToonShaderDotted,
} from 'three/addons/shaders/ToonShader.js';

export const MARCHING_CUBES_SCENE_ID = 'marching_cubes';

/** Matches initial camera (-500, 500, 1500) → origin; OrbitControls min/max distance. */
const MARCHING_ORBIT_ZOOM_DEFAULT = Math.hypot(500, 500, 1500);

const _orbitZoomDir = new THREE.Vector3();

const MATERIAL_KEYS = [
  'shiny',
  'chrome',
  'liquid',
  'matte',
  'flat',
  'textured',
  'colors',
  'multiColors',
  'plastic',
  'toon1',
  'toon2',
  'hatching',
  'dotted',
];

export const sceneMeta = {
  id: MARCHING_CUBES_SCENE_ID,
  label: 'Marching cubes',
  defaultBindings: {},
  paramSpecs: {
    speed: { min: 0.1, max: 8, label: 'Speed' },
    numBlobs: { min: 1, max: 50, step: 1, label: 'Blob count' },
    resolution: { min: 14, max: 100, step: 1, label: 'Field resolution' },
    isolation: { min: 10, max: 300, label: 'Isolation' },
    material: { label: 'Material', options: [...MATERIAL_KEYS] },
    floor: { label: 'Floor plane' },
    wallx: { label: 'Wall X' },
    wallz: { label: 'Wall Z' },
    effectScale: { min: 200, max: 1500, step: 10, label: 'Mesh scale' },
    fieldSubtract: { min: 1, max: 50, step: 1, label: 'Field subtract' },
    blobStrength: { min: 0.1, max: 4, label: 'Blob strength' },
    zoom: { min: 500, max: 5000, label: 'Camera distance' },
  },
  defaultParams: {
    speed: 1,
    numBlobs: 10,
    resolution: 28,
    isolation: 80,
    material: 'shiny',
    floor: true,
    wallx: false,
    wallz: false,
    effectScale: 700,
    fieldSubtract: 12,
    blobStrength: 1.2,
    zoom: MARCHING_ORBIT_ZOOM_DEFAULT,
  },
  bindingTargets: [
    { key: 'speed', label: 'Animation speed', min: 0, max: 1 },
    { key: 'numBlobs', label: 'Blob count', min: 0, max: 1 },
    { key: 'isolation', label: 'Surface isolation', min: 0, max: 1 },
    { key: 'resolution', label: 'Field resolution', min: 0, max: 1 },
    { key: 'material', label: 'Material', min: 0, max: 1 },
    { key: 'zoom', label: 'Camera distance', min: 0, max: 1 },
    { key: 'floor', label: 'Floor plane', min: 0, max: 1 },
    { key: 'wallx', label: 'Wall X', min: 0, max: 1 },
    { key: 'wallz', label: 'Wall Z', min: 0, max: 1 },
    { key: 'effectScale', label: 'Mesh scale', min: 0, max: 1 },
    { key: 'fieldSubtract', label: 'Field subtract', min: 0, max: 1 },
    { key: 'blobStrength', label: 'Blob strength', min: 0, max: 1 },
  ],
};

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function parseColorHex(hex) {
  const s = String(hex || '#ffffff').replace('#', '');
  const n = parseInt(s.length === 3 ? [...s].map((c) => c + c).join('') : s, 16);
  return Number.isFinite(n) ? n : 0xffffff;
}

function mapBind01(resolved, key, min, max, manual) {
  if (resolved[key] === undefined) return manual;
  return min + clamp01(resolved[key]) * (max - min);
}

function mapBindBool(resolved, key, manual) {
  if (resolved[key] === undefined) return manual;
  return resolved[key] > 0.5;
}

function mapBindMaterial(resolved, key, manualKey) {
  if (resolved[key] === undefined) return manualKey;
  const idx = Math.min(
    MATERIAL_KEYS.length - 1,
    Math.floor(clamp01(resolved[key]) * MATERIAL_KEYS.length)
  );
  return MATERIAL_KEYS[idx];
}

function createShaderMaterial(shader, light, ambientLight) {
  const u = THREE.UniformsUtils.clone(shader.uniforms);
  const material = new THREE.ShaderMaterial({
    uniforms: u,
    vertexShader: shader.vertexShader,
    fragmentShader: shader.fragmentShader,
  });
  material.uniforms.uDirLightPos.value = light.position;
  material.uniforms.uDirLightColor.value = light.color;
  material.uniforms.uAmbientLightColor.value = ambientLight.color;
  return material;
}

/**
 * Port of three.js webgl_marchingcubes example (metaballs + optional planes).
 */
export class MarchingCubesScene {
  constructor() {
    this.threeScene = new THREE.Scene();
    this.threeScene.background = new THREE.Color(0x050505);

    this._perspCamera = null;
    this._controls = null;
    this._canvas = null;

    this._directionalLight = null;
    this._pointLight = null;
    this._ambientLight = null;

    this._materials = null;
    this._reflectionCube = null;
    this._refractionCube = null;
    this._uvTexture = null;

    this._effect = null;
    this._resolution = 28;
    this._currentMaterialKey = 'shiny';
    this._time = 0;

    this._ctrl = {};

    this._params = { ...sceneMeta.defaultParams };
    this._rebuildCtrlFromParams();
    this._inited = false;
    this._ready = false;
    this._loadGen = 0;
  }

  getMidiTargets() {
    return sceneMeta.bindingTargets;
  }

  getSceneLabel() {
    return sceneMeta.label;
  }

  getCamera() {
    return this._perspCamera;
  }

  /**
   * @param {Record<string, unknown>} params
   */
  applyParams(params = {}) {
    this._params = { ...sceneMeta.defaultParams, ...params };
    this._rebuildCtrlFromParams();
    this._syncSceneFromParams();
  }

  _rebuildCtrlFromParams() {
    const p = this._params;
    this._ctrl.speed = Number(p.speed) || 1;
    this._ctrl.numBlobs = Math.round(Number(p.numBlobs) || 10);
    this._ctrl.resolution = Math.round(Number(p.resolution) || 28);
    this._ctrl.isolation = Number(p.isolation) || 80;
    this._ctrl.floor = p.floor !== false;
    this._ctrl.wallx = p.wallx === true;
    this._ctrl.wallz = p.wallz === true;
    this._ctrl.fieldSubtract = Math.round(
      THREE.MathUtils.clamp(Number(p.fieldSubtract) || 12, 1, 80)
    );
    this._ctrl.blobStrength = THREE.MathUtils.clamp(Number(p.blobStrength) || 1.2, 0.05, 8);
    const mat = String(p.material || 'shiny').trim();
    if (MATERIAL_KEYS.includes(mat)) {
      this._currentMaterialKey = mat;
    }
  }

  _syncSceneFromParams() {
    if (this._directionalLight) {
      const i = Number(this._params.dirLightIntensity);
      this._directionalLight.intensity = Number.isFinite(i) ? i : 3;
      this._directionalLight.color.setHex(parseColorHex(this._params.dirLightColor));
      this._directionalLight.position.set(
        Number(this._params.dirLightPosX) || 0,
        Number(this._params.dirLightPosY) || 0,
        Number(this._params.dirLightPosZ) || 1
      );
    }
    if (this._pointLight) {
      const i = Number(this._params.pointLightIntensity);
      this._pointLight.intensity = Number.isFinite(i) ? i : 3;
      this._pointLight.color.setHex(parseColorHex(this._params.pointLightColor));
      this._pointLight.position.set(
        Number(this._params.pointLightPosX) || 0,
        Number(this._params.pointLightPosY) || 0,
        Number(this._params.pointLightPosZ) || 100
      );
    }
    if (this._ambientLight) {
      const i = Number(this._params.ambientIntensity);
      this._ambientLight.intensity = Number.isFinite(i) ? i : 3;
      this._ambientLight.color.setHex(parseColorHex(this._params.ambientColor));
    }
    if (this.threeScene?.background instanceof THREE.Color) {
      this.threeScene.background.setHex(parseColorHex(this._params.backgroundColor));
    } else if (this.threeScene) {
      this.threeScene.background = new THREE.Color(parseColorHex(this._params.backgroundColor));
    }
    if (this._controls) {
      const omin = Number(this._params.orbitMinDistance);
      const omax = Number(this._params.orbitMaxDistance);
      if (Number.isFinite(omin) && Number.isFinite(omax) && omin > 0 && omax > omin + 1) {
        this._controls.minDistance = omin;
        this._controls.maxDistance = omax;
      }
    }
    if (this._perspCamera) {
      const fov = Number(this._params.cameraFov);
      if (Number.isFinite(fov)) {
        this._perspCamera.fov = THREE.MathUtils.clamp(fov, 10, 120);
        this._perspCamera.updateProjectionMatrix();
      }
    }
    if (this._effect) {
      const s = Number(this._params.effectScale);
      const sc = Number.isFinite(s) ? THREE.MathUtils.clamp(s, 50, 2500) : 700;
      this._effect.scale.setScalar(sc);
    }
    if (this._ready && this._effect && this._materials) {
      this._setMaterial(this._currentMaterialKey);
    }
    this._applyOrbitZoom();
  }

  /**
   * @param {number | null} [overrideDist] Camera–target distance; uses `params.zoom` when nullish.
   */
  _applyOrbitZoom(overrideDist = null) {
    if (!this._perspCamera || !this._controls) return;
    const zmin = Number(this._params.orbitMinDistance);
    const zmax = Number(this._params.orbitMaxDistance);
    const lo = Number.isFinite(zmin) && zmin > 0 ? zmin : 500;
    const hi = Number.isFinite(zmax) && zmax > lo ? zmax : Math.max(lo + 1, 5000);
    const raw = overrideDist != null ? overrideDist : Number(this._params.zoom);
    const dist = Number.isFinite(raw)
      ? THREE.MathUtils.clamp(raw, lo, hi)
      : MARCHING_ORBIT_ZOOM_DEFAULT;
    const target = this._controls.target;
    const cam = this._perspCamera.position;
    _orbitZoomDir.subVectors(cam, target);
    if (_orbitZoomDir.lengthSq() < 1e-6) {
      _orbitZoomDir.set(-500, 500, 1500);
    }
    _orbitZoomDir.normalize().multiplyScalar(dist);
    cam.copy(target).add(_orbitZoomDir);
  }

  /**
   * @param {{ getSize?: () => { w: number, h: number }, canvas?: HTMLCanvasElement | null }} [ctx]
   */
  init(ctx) {
    if (this._inited) return;
    this._inited = true;

    this._canvas = ctx?.canvas ?? document.getElementById('main-canvas');
    const { w, h } = ctx?.getSize?.() ?? {
      w: window.innerWidth,
      h: window.innerHeight,
    };

    this._perspCamera = new THREE.PerspectiveCamera(45, w / Math.max(h, 1), 1, 10000);
    this._perspCamera.position.set(-500, 500, 1500);

    this._directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    this._directionalLight.position.set(0.5, 0.5, 1);
    this.threeScene.add(this._directionalLight);

    this._pointLight = new THREE.PointLight(0xffffff, 1, 0, 0);
    this._pointLight.position.set(0, 0, 100);
    this.threeScene.add(this._pointLight);

    this._ambientLight = new THREE.AmbientLight(0xffffff, 1);
    this.threeScene.add(this._ambientLight);

    if (this._canvas) {
      this._controls = new OrbitControls(this._perspCamera, this._canvas);
    }

    this._syncSceneFromParams();
    this._startLoadTextures();
  }

  _texBase() {
    return `${import.meta.env.BASE_URL}textures/`;
  }

  _startLoadTextures() {
    const gen = ++this._loadGen;
    const cubeFolder = `${this._texBase()}cube/SwedishRoyalCastle/`;
    const cubeLoader = new THREE.CubeTextureLoader();
    const urls = ['px.jpg', 'nx.jpg', 'py.jpg', 'ny.jpg', 'pz.jpg', 'nz.jpg'];
    cubeLoader.setPath(cubeFolder);
    cubeLoader.load(
      urls,
      (reflectionCube) => {
        if (gen !== this._loadGen) {
          reflectionCube.dispose?.();
          return;
        }
        const refractionCube = reflectionCube.clone();
        refractionCube.mapping = THREE.CubeRefractionMapping;

        const uvUrl = `${this._texBase()}uv_grid_opengl.jpg`;
        new THREE.TextureLoader().load(
          uvUrl,
          (tex) => {
            if (gen !== this._loadGen) {
              tex.dispose();
              return;
            }
            tex.wrapS = THREE.RepeatWrapping;
            tex.wrapT = THREE.RepeatWrapping;
            tex.colorSpace = THREE.SRGBColorSpace;
            this._finishAssetLoad(gen, reflectionCube, refractionCube, tex);
          },
          undefined,
          () => {
            if (gen !== this._loadGen) return;
            this._finishAssetLoad(gen, reflectionCube, refractionCube, null);
          }
        );
      },
      undefined,
      () => {
        if (gen !== this._loadGen) return;
        this._finishAssetLoad(gen, null, null, null);
      }
    );
  }

  _finishAssetLoad(gen, reflectionCube, refractionCube, uvTexture) {
    if (gen !== this._loadGen) return;

    this._reflectionCube = reflectionCube;
    this._refractionCube = refractionCube;
    this._uvTexture = uvTexture;

    this._materials = this._generateMaterials(reflectionCube, refractionCube, uvTexture);

    const res = Math.floor(this._ctrl.resolution);
    this._resolution = res;
    const mat =
      this._materials[this._currentMaterialKey] || this._materials.shiny;
    this._effect = new MarchingCubes(res, mat, true, true, 100000);
    this._effect.position.set(0, 0, 0);
    this._effect.scale.set(700, 700, 700);
    this._setMaterial(this._currentMaterialKey);
    this._effect.isolation = this._ctrl.isolation;

    this.threeScene.add(this._effect);
    this._ready = true;
    this._syncSceneFromParams();
  }

  /**
   * @param {THREE.CubeTexture | null} reflectionCube
   * @param {THREE.CubeTexture | null} refractionCube
   * @param {THREE.Texture | null} uvTexture
   */
  _generateMaterials(reflectionCube, refractionCube, uvTexture) {
    const light = this._directionalLight;
    const ambient = this._ambientLight;

    const toon1 = createShaderMaterial(ToonShader1, light, ambient);
    const toon2 = createShaderMaterial(ToonShader2, light, ambient);
    const hatching = createShaderMaterial(ToonShaderHatching, light, ambient);
    const dotted = createShaderMaterial(ToonShaderDotted, light, ambient);

    const hasReflect = reflectionCube != null;
    const hasRefract = refractionCube != null;

    const texture = uvTexture;
    const texturedMat =
      texture != null
        ? new THREE.MeshPhongMaterial({
            color: 0xffffff,
            specular: 0x111111,
            shininess: 1,
            map: texture,
          })
        : new THREE.MeshPhongMaterial({
            color: 0xffffff,
            specular: 0x111111,
            shininess: 1,
          });

    return {
      shiny: new THREE.MeshStandardMaterial({
        color: 0x9c0000,
        envMap: hasReflect ? reflectionCube : null,
        roughness: 0.1,
        metalness: 1.0,
      }),
      chrome: new THREE.MeshLambertMaterial({
        color: 0xffffff,
        envMap: hasReflect ? reflectionCube : null,
      }),
      liquid: new THREE.MeshLambertMaterial({
        color: 0xffffff,
        envMap: hasRefract ? refractionCube : hasReflect ? reflectionCube : null,
        refractionRatio: 0.85,
      }),
      matte: new THREE.MeshPhongMaterial({ specular: 0x494949, shininess: 1 }),
      flat: new THREE.MeshStandardMaterial({
        color: 0xcccccc,
        flatShading: true,
      }),
      textured: texturedMat,
      colors: new THREE.MeshPhongMaterial({
        color: 0xffffff,
        specular: 0xffffff,
        shininess: 2,
        vertexColors: true,
      }),
      multiColors: new THREE.MeshPhongMaterial({
        shininess: 2,
        vertexColors: true,
      }),
      plastic: new THREE.MeshPhongMaterial({
        specular: 0xc1c1c1,
        shininess: 250,
      }),
      toon1: toon1,
      toon2: toon2,
      hatching: hatching,
      dotted: dotted,
    };
  }

  /**
   * @param {string} id
   */
  _setMaterial(id) {
    if (!this._effect || !this._materials) return;
    const m = this._materials[id];
    if (!m) return;
    this._currentMaterialKey = id;
    this._effect.material = m;
    this._effect.enableUvs = id === 'textured';
    this._effect.enableColors = id === 'colors' || id === 'multiColors';
  }

  /**
   * @param {MarchingCubes} object
   * @param {number} time
   * @param {number} numblobs
   * @param {boolean} floor
   * @param {boolean} wallx
   * @param {boolean} wallz
   * @param {number} [fieldSubtract]
   * @param {number} [blobStrength]
   */
  _updateCubes(object, time, numblobs, floor, wallx, wallz, fieldSubtract, blobStrength) {
    object.reset();

    const rainbow = [
      new THREE.Color(0xff0000),
      new THREE.Color(0xffbb00),
      new THREE.Color(0xffff00),
      new THREE.Color(0x00ff00),
      new THREE.Color(0x0000ff),
      new THREE.Color(0x9400bd),
      new THREE.Color(0xc800eb),
    ];
    const subtract = fieldSubtract ?? this._ctrl.fieldSubtract;
    const bs = blobStrength ?? this._ctrl.blobStrength;
    const strength = (bs * 1.2) / ((Math.sqrt(numblobs) - 1) / 4 + 1);

    for (let i = 0; i < numblobs; i++) {
      const ballx =
        Math.sin(i + 1.26 * time * (1.03 + 0.5 * Math.cos(0.21 * i))) * 0.27 + 0.5;
      const bally =
        Math.abs(Math.cos(i + 1.12 * time * Math.cos(1.22 + 0.1424 * i))) * 0.77;
      const ballz =
        Math.cos(i + 1.32 * time * 0.1 * Math.sin(0.92 + 0.53 * i)) * 0.27 + 0.5;

      if (this._currentMaterialKey === 'multiColors') {
        object.addBall(ballx, bally, ballz, strength, subtract, rainbow[i % 7]);
      } else {
        object.addBall(ballx, bally, ballz, strength, subtract);
      }
    }

    if (floor) object.addPlaneY(2, 12);
    if (wallz) object.addPlaneZ(2, 12);
    if (wallx) object.addPlaneX(2, 12);

    object.update();
  }

  /**
   * @param {number} dt
   * @param {Record<string, number>} resolved
   */
  update(dt, resolved = {}) {
    this._controls?.update();

    if (!this._ready || !this._effect) return;

    const zmin = Number(this._params.orbitMinDistance);
    const zmax = Number(this._params.orbitMaxDistance);
    const orbitLo = Number.isFinite(zmin) && zmin > 0 ? zmin : 500;
    const orbitHi = Number.isFinite(zmax) && zmax > orbitLo ? zmax : Math.max(orbitLo + 1, 5000);

    let speed = this._ctrl.speed;
    if (resolved.speed !== undefined) {
      speed = 0.1 + clamp01(resolved.speed) * 7.9;
    }
    let numBlobs = this._ctrl.numBlobs;
    if (resolved.numBlobs !== undefined) {
      numBlobs = Math.round(1 + clamp01(resolved.numBlobs) * 49);
    }
    let isolation = this._ctrl.isolation;
    if (resolved.isolation !== undefined) {
      isolation = 10 + clamp01(resolved.isolation) * 290;
    }

    let targetRes = Math.floor(this._ctrl.resolution);
    if (resolved.resolution !== undefined) {
      targetRes = Math.round(14 + clamp01(resolved.resolution) * 86);
    }

    const matKey = mapBindMaterial(resolved, 'material', this._currentMaterialKey);
    if (matKey !== this._currentMaterialKey) {
      this._setMaterial(matKey);
    }

    let floor = this._ctrl.floor;
    let wallx = this._ctrl.wallx;
    let wallz = this._ctrl.wallz;
    if (resolved.floor !== undefined) floor = mapBindBool(resolved, 'floor', floor);
    if (resolved.wallx !== undefined) wallx = mapBindBool(resolved, 'wallx', wallx);
    if (resolved.wallz !== undefined) wallz = mapBindBool(resolved, 'wallz', wallz);

    let fieldSubtract = this._ctrl.fieldSubtract;
    if (resolved.fieldSubtract !== undefined) {
      fieldSubtract = Math.round(mapBind01(resolved, 'fieldSubtract', 1, 50, fieldSubtract));
    }
    let blobStrength = this._ctrl.blobStrength;
    if (resolved.blobStrength !== undefined) {
      blobStrength = mapBind01(resolved, 'blobStrength', 0.1, 4, blobStrength);
    }

    let effectScale = Number(this._params.effectScale) || 700;
    effectScale = THREE.MathUtils.clamp(effectScale, 50, 2500);
    if (resolved.effectScale !== undefined) {
      effectScale = mapBind01(resolved, 'effectScale', 200, 1500, effectScale);
    }
    this._effect.scale.setScalar(effectScale);

    if (resolved.dirLightIntensity !== undefined) {
      const manual = Number(this._params.dirLightIntensity);
      const v = mapBind01(resolved, 'dirLightIntensity', 0, 12, Number.isFinite(manual) ? manual : 3);
      if (this._directionalLight) this._directionalLight.intensity = v;
    }
    if (resolved.pointLightIntensity !== undefined) {
      const manual = Number(this._params.pointLightIntensity);
      const v = mapBind01(
        resolved,
        'pointLightIntensity',
        0,
        12,
        Number.isFinite(manual) ? manual : 3
      );
      if (this._pointLight) this._pointLight.intensity = v;
    }
    if (resolved.ambientIntensity !== undefined) {
      const manual = Number(this._params.ambientIntensity);
      const v = mapBind01(resolved, 'ambientIntensity', 0, 12, Number.isFinite(manual) ? manual : 3);
      if (this._ambientLight) this._ambientLight.intensity = v;
    }
    if (resolved.pointLightPosZ !== undefined) {
      const baseZ = Number(this._params.pointLightPosZ);
      const manualZ = Number.isFinite(baseZ) ? baseZ : 100;
      const pz = mapBind01(resolved, 'pointLightPosZ', -400, 600, manualZ);
      if (this._pointLight) this._pointLight.position.z = pz;
    }

    let camFov = Number(this._params.cameraFov) || 45;
    if (resolved.cameraFov !== undefined) {
      camFov = mapBind01(resolved, 'cameraFov', 20, 90, camFov);
    }
    if (this._perspCamera) {
      this._perspCamera.fov = THREE.MathUtils.clamp(camFov, 10, 120);
      this._perspCamera.updateProjectionMatrix();
    }

    let camDist = Number(this._params.zoom);
    if (resolved.zoom !== undefined) {
      camDist = orbitLo + clamp01(resolved.zoom) * (orbitHi - orbitLo);
    }
    this._applyOrbitZoom(Number.isFinite(camDist) ? camDist : null);

    this._time += dt * speed * 0.5;

    if (targetRes !== this._resolution) {
      this._resolution = targetRes;
      this._effect.init(targetRes);
    }

    if (Math.abs(isolation - this._effect.isolation) > 0.01) {
      this._effect.isolation = isolation;
    }

    this._updateCubes(this._effect, this._time, numBlobs, floor, wallx, wallz, fieldSubtract, blobStrength);
  }

  /**
   * @param {number} w
   * @param {number} h
   */
  resize(w, h) {
    if (!this._perspCamera) return;
    this._perspCamera.aspect = w / Math.max(h, 1);
    this._perspCamera.updateProjectionMatrix();
  }

  dispose() {
    this._loadGen++;

    this._controls?.dispose();
    this._controls = null;

    if (this._effect) {
      this._effect.geometry?.dispose();
      this.threeScene.remove(this._effect);
      this._effect = null;
    }

    if (this._materials) {
      for (const key of MATERIAL_KEYS) {
        const m = this._materials[key];
        if (!m) continue;
        if (m.map) m.map.dispose();
        m.dispose?.();
      }
      this._materials = null;
    }

    this._refractionCube = null;
    this._reflectionCube?.dispose();
    this._uvTexture?.dispose();
    this._reflectionCube = null;
    this._refractionCube = null;
    this._uvTexture = null;

    if (this._directionalLight) this.threeScene.remove(this._directionalLight);
    if (this._pointLight) this.threeScene.remove(this._pointLight);
    if (this._ambientLight) this.threeScene.remove(this._ambientLight);
    this._directionalLight = null;
    this._pointLight = null;
    this._ambientLight = null;

    this._perspCamera = null;
    this._ready = false;
    this._inited = false;
  }
}
